import createTheme from "@mui/material/styles/createTheme"
import MuiThemeProvider from "@mui/material/styles/ThemeProvider"
import { ConfigProvider as AntdThemeProvider } from "antd"
import { getStyle } from "antd/es/config-provider/cssVariables"
import {
  type ReactElement,
  type ReactNode,
  createContext,
  useEffect,
  useState,
} from "react"

import type { CustomizationOptions } from "@/api/customization"
import { type Hex, type OneSchemaStyles, defaultStyles } from "@/styles/defaultStyles"
import { Feature, isFeatureEnabled } from "@/util/gating"
import { Head } from "@/util/Head"
import {
  COLOR_REGEX,
  addOpacity,
  convertOpacityToHexColor,
  darken,
  stripAlphaFromHex,
} from "@/util/styles"

interface ThemeContextState {
  styles: OneSchemaStyles
  customStylesheetUrl: string | null
  applyStyleOverrides: (options: CustomizationOptions) => void
}

export interface ThemeProviderProps {
  children: ReactNode
  parent?: HTMLElement | null
}

function applyAntdTheme(styles: OneSchemaStyles, parent: HTMLElement) {
  // antd's support for themes is experimental
  // we need more flexibility to be able to apply them
  // as a style field on an HTMLElement
  // so we have to use the function antd uses
  // to make its themes and 'parse' it
  // this function is not publicly exposed, had to
  // go through their source to find it.
  const antStyles = getStyle("ant", {
    primaryColor: styles.ColorPrimary100,
    successColor: styles.ColorSuccessGreen100,
    errorColor: styles.ColorErrorRed100,
    warningColor: styles.ColorWarningYellow100,
  })
  // the return wraps the keys in 'root: { }` for putting in <style>
  // but we want to put on a div, so we parse:
  const start = antStyles.indexOf("{") + 1
  const end = antStyles.indexOf("}")
  const antProps = antStyles.substring(start, end).trim().split(";")
  antProps.forEach((property) => {
    const [key, value] = property.split(":")
    if (key && value) {
      parent.style.setProperty(key.trim(), value.trim())
    }
  })
}

// Util function for parsing the styles from a customization object into the proper hash
// of overrides for defaultStyles.
// Color fields that should be interpreted as opaque. Alpha channels are stripped so
// derived colors (hover states, transparent variants, etc.) are computed correctly.
const OPAQUE_COLOR_FIELDS: (keyof CustomizationOptions)[] = [
  "primaryColor",
  "backgroundPrimaryColor",
  "backgroundSecondaryColor",
  "headerColor",
  "footerColor",
  "borderColor",
  "successColor",
  "warningColor",
  "errorColor",
  "fontColorPrimary",
  "fontColorSecondary",
  "fontColorPlaceholder",
  "buttonPrimaryFillColor",
  "buttonPrimaryStrokeColor",
  "buttonPrimaryTextColor",
  "buttonSecondaryFillColor",
  "buttonSecondaryStrokeColor",
  "buttonSecondaryTextColor",
  "buttonTertiaryFillColor",
  "buttonTertiaryStrokeColor",
  "buttonTertiaryTextColor",
  "buttonAlertFillColor",
  "buttonAlertStrokeColor",
  "buttonAlertTextColor",
]

function processCustomizationStyles(
  rawStyles: CustomizationOptions,
): Partial<OneSchemaStyles> {
  const overrides: Partial<OneSchemaStyles> = {}

  // Strip alpha from opaque color fields so the existing 3/6-char regex checks and
  // derived-color logic work unchanged. modalMaskColor preserves its alpha channel.
  const normalizedStyles = { ...rawStyles } as Record<string, unknown>
  OPAQUE_COLOR_FIELDS.forEach((field) => {
    const value = normalizedStyles[field as string]
    if (typeof value === "string" && value.startsWith("#")) {
      normalizedStyles[field as string] = stripAlphaFromHex(value as Hex)
    }
  })
  const styles = normalizedStyles as CustomizationOptions

  const backgroundColor =
    styles.backgroundPrimaryColor && COLOR_REGEX.test(styles.backgroundPrimaryColor)
      ? styles.backgroundPrimaryColor
      : defaultStyles.ColorBackgroundPrimary

  /******************
   * GENERAL COLORS *
   ******************/

  // function to apply override to all relevant colors
  // based on given key and values
  const overrideColor = (
    override: Hex,
    baseName: string,
    numbers: number[],
    modifier?: string,
  ) => {
    let transform = convertOpacityToHexColor
    if (modifier === "Transparent") {
      transform = addOpacity
    }

    numbers.forEach((number) => {
      // access color names based on string key
      // using naming conventions ie. 'ColorPrimaryTransparent10'
      overrides[`${baseName}${modifier || ""}${number}` as keyof OneSchemaStyles] =
        number > 100
          ? darken(override, (number - 100) / 100)
          : transform(override, number / 100, backgroundColor)
    })
  }

  if (styles.primaryColor && COLOR_REGEX.test(styles.primaryColor)) {
    overrideColor(styles.primaryColor, "ColorPrimary", [100, 80, 20, 15, 10])
    overrideColor(styles.primaryColor, "ColorPrimary", [15, 10, 8, 5], "Transparent")
  }

  if (styles.successColor && COLOR_REGEX.test(styles.successColor)) {
    overrideColor(styles.successColor, "ColorSuccessGreen", [120, 100, 70, 40, 20])
  }

  if (styles.warningColor && COLOR_REGEX.test(styles.warningColor)) {
    overrideColor(styles.warningColor, "ColorWarningYellow", [120, 100, 40, 20])
    overrideColor(styles.warningColor, "ColorWarningYellow", [30, 25, 20], "Transparent")
  }

  if (styles.errorColor && COLOR_REGEX.test(styles.errorColor)) {
    overrideColor(styles.errorColor, "ColorErrorRed", [120, 100, 70, 15, 10])
    overrideColor(styles.errorColor, "ColorErrorRed", [30, 25, 20], "Transparent")
  }

  /********************************
   * MODAL *
   ********************************/

  if (styles.modalMaskColor) {
    overrides.ColorModalMask = styles.modalMaskColor
  }

  if (styles.modalBorderRadius) {
    overrides.ModalBorderRadius = styles.modalBorderRadius
  }

  /********************************
   * HEADER / FOOTER / BACKGROUND *
   ********************************/
  if (styles.headerColor && COLOR_REGEX.test(styles.headerColor)) {
    overrides.ColorHeader = styles.headerColor
  }
  if (styles.footerColor && COLOR_REGEX.test(styles.footerColor)) {
    overrides.ColorFooter = styles.footerColor
  }
  if (styles.borderColor && COLOR_REGEX.test(styles.borderColor)) {
    overrides.ColorBorder = styles.borderColor
  }
  if (styles.backgroundPrimaryColor && COLOR_REGEX.test(styles.backgroundPrimaryColor)) {
    const primaryBackgroundColor = styles.backgroundPrimaryColor
    overrides.ColorBackgroundPrimary = primaryBackgroundColor
    overrides.ColorBackgroundPrimaryDark02 = darken(primaryBackgroundColor, 0.02)
  }
  if (
    styles.backgroundSecondaryColor &&
    COLOR_REGEX.test(styles.backgroundSecondaryColor)
  ) {
    const secondaryBackgroundColor = styles.backgroundSecondaryColor
    overrides.ColorBackgroundSecondary = secondaryBackgroundColor
    overrides.ColorBackgroundSecondaryDark02 = darken(secondaryBackgroundColor, 0.02)
    overrideColor(
      styles.backgroundSecondaryColor,
      "ColorBackgroundSecondary",
      [0],
      "Transparent",
    )
  }

  /*********
   * FONTS *
   *********/

  if (styles.fontFamily && styles.fontUrl) {
    overrides.FontFamily = styles.fontFamily
    overrides.FontUrl = styles.fontUrl
  }

  if (styles.fontColorPrimary && COLOR_REGEX.test(styles.fontColorPrimary)) {
    overrides.FontColorPrimary = styles.fontColorPrimary
  }
  if (styles.fontColorSecondary && COLOR_REGEX.test(styles.fontColorSecondary)) {
    overrides.FontColorSecondary = styles.fontColorSecondary
  }
  if (styles.fontColorPlaceholder && COLOR_REGEX.test(styles.fontColorPlaceholder)) {
    overrides.FontColorPlaceholder = styles.fontColorPlaceholder
  }

  /***********
   * BUTTONS *
   ***********/
  if (styles.buttonBorderRadius) {
    overrides.ButtonBorderRadius = styles.buttonBorderRadius
  }
  // Primary Button
  if (styles.buttonPrimaryFillColor && COLOR_REGEX.test(styles.buttonPrimaryFillColor)) {
    const primaryFillColor = styles.buttonPrimaryFillColor
    overrides.ButtonPrimaryFillColor = primaryFillColor
    overrides.ButtonPrimaryFillColorHover = darken(primaryFillColor, 0.2)
  }
  if (
    styles.buttonPrimaryStrokeColor &&
    COLOR_REGEX.test(styles.buttonPrimaryStrokeColor)
  ) {
    overrides.ButtonPrimaryStrokeColor = styles.buttonPrimaryStrokeColor
  }
  if (styles.buttonPrimaryTextColor && COLOR_REGEX.test(styles.buttonPrimaryTextColor)) {
    overrides.ButtonPrimaryTextColor = styles.buttonPrimaryTextColor
  }
  // Secondary Button
  if (
    styles.buttonSecondaryFillColor &&
    COLOR_REGEX.test(styles.buttonSecondaryFillColor)
  ) {
    const secondaryFillColor = styles.buttonSecondaryFillColor
    overrides.ButtonSecondaryFillColor = secondaryFillColor
    overrides.ButtonSecondaryFillColorHover = darken(secondaryFillColor, 0.2)
  }
  if (
    styles.buttonSecondaryStrokeColor &&
    COLOR_REGEX.test(styles.buttonSecondaryStrokeColor)
  ) {
    overrides.ButtonSecondaryStrokeColor = styles.buttonSecondaryStrokeColor
  }
  if (
    styles.buttonSecondaryTextColor &&
    COLOR_REGEX.test(styles.buttonSecondaryTextColor)
  ) {
    overrides.ButtonSecondaryTextColor = styles.buttonSecondaryTextColor
  }
  // Tertiary Button
  if (
    styles.buttonTertiaryFillColor &&
    COLOR_REGEX.test(styles.buttonTertiaryFillColor)
  ) {
    const tertiaryFillColor = styles.buttonTertiaryFillColor
    overrides.ButtonTertiaryFillColor = tertiaryFillColor
    overrides.ButtonTertiaryFillColorHover = darken(tertiaryFillColor, 0.02)
  }
  if (
    styles.buttonTertiaryStrokeColor &&
    COLOR_REGEX.test(styles.buttonTertiaryStrokeColor)
  ) {
    overrides.ButtonTertiaryStrokeColor = styles.buttonTertiaryStrokeColor
  }
  if (
    styles.buttonTertiaryTextColor &&
    COLOR_REGEX.test(styles.buttonTertiaryTextColor)
  ) {
    overrides.ButtonTertiaryTextColor = styles.buttonTertiaryTextColor
  }
  // Alert Button
  if (styles.buttonAlertFillColor && COLOR_REGEX.test(styles.buttonAlertFillColor)) {
    const alertFillColor = styles.buttonAlertFillColor
    overrides.ButtonAlertFillColor = alertFillColor
    overrides.ButtonAlertFillColorHover = darken(alertFillColor, 0.05)
  }
  if (styles.buttonAlertStrokeColor && COLOR_REGEX.test(styles.buttonAlertStrokeColor)) {
    overrides.ButtonAlertStrokeColor = styles.buttonAlertStrokeColor
  }
  if (styles.buttonAlertTextColor && COLOR_REGEX.test(styles.buttonAlertTextColor)) {
    overrides.ButtonAlertTextColor = styles.buttonAlertTextColor
  }

  return overrides
}

export const ThemeContext = createContext({} as ThemeContextState)

// Provider that manages the Styles object. If we are in an embed org, use a mix of the
// defaultStyles overriden by the styles object from customization. Otherwise (eg. in
// workspaces), just use the defaultStyles.
export function ThemeProvider(props: ThemeProviderProps): ReactElement | null {
  const [styles, setStyles] = useState<OneSchemaStyles>(defaultStyles)
  const [customStylesheetUrl, setCustomStylesheetUrl] = useState<string | null>(null)
  const isCustomStylesheetEnabled = isFeatureEnabled(
    Feature.CustomizationCustomStylesheet,
  )
  const setCustomization = (options: CustomizationOptions) => {
    const overrides = processCustomizationStyles(options)
    setStyles({ ...defaultStyles, ...overrides })
    if (isCustomStylesheetEnabled && "customStylesheetUrl" in options) {
      setCustomStylesheetUrl(options.customStylesheetUrl || null)
    }
  }

  useEffect(() => {
    if (props.parent) {
      Object.keys(styles).forEach((key: string) => {
        props.parent!.style.setProperty(`--${key}`, styles[key as keyof OneSchemaStyles])
      })

      applyAntdTheme(styles, props.parent)

      // Overrides AntD Styles and anything not using new Typography changes to follow font
      // family from styles.
      props.parent.style.fontFamily = styles["FontFamily"]
      props.parent.style.color = styles["FontColorPrimary"]
    }
  }, [styles, props.parent])

  const muiTheme = createTheme({
    palette: {
      primary: {
        main: styles.ColorPrimary100,
      },
      secondary: {
        main: styles.ColorSecondary100,
      },
      error: {
        main: styles.ColorErrorRed100,
      },
      success: {
        main: styles.ColorSuccessGreen100,
      },
    },
  })

  return (
    <ThemeContext.Provider
      value={{ styles, customStylesheetUrl, applyStyleOverrides: setCustomization }}
    >
      <AntdThemeProvider
        getPopupContainer={(trigger) =>
          trigger?.closest<HTMLElement>(
            ".ant-modal-content, .ant-drawer-content-wrapper",
          ) ?? document.body
        }
      >
        <MuiThemeProvider theme={muiTheme}>
          <Head>
            <style>
              @import url({styles.FontUrl}); @import url({styles.FontMonoUrl});
            </style>
            {customStylesheetUrl && (
              <link rel="stylesheet" type="text/css" href={customStylesheetUrl} />
            )}
          </Head>
          {props.children}
        </MuiThemeProvider>
      </AntdThemeProvider>
    </ThemeContext.Provider>
  )
}
